import type { ReactNode } from 'react';
import { useCallback } from 'react';
import PNV from '@components/PositiveNegativeValue';
import { Card, Name, Returns, ButtonLink, Details } from './styled';
import { RouterLink } from '@redux-first-router-link';
import { useTracking } from 'react-tracking';
import { useMarketIndustryUrlGetter } from '@/pages/Market/hooks/useMarketIndustryUrlGetter';
import { useReduxForDashboard } from '../../hooks/useReduxForDashboard';

export interface Props {
  name: string;
  slug?: string;
  id: string;
  index: number;
  return7D: number | null;
  return7DFormatted: string;
  return1YrAbs: number | null;
  return1YrAbsFormatted: string;
}

const Click = ({
  id,
  name,
  slug,
  index,
  children,
}: Pick<Props, 'id' | 'name' | 'slug' | 'index'> & { children: ReactNode }) => {
  const canonicalUrl = useMarketIndustryUrlGetter(id);
  const { navigateToScreener } = useReduxForDashboard();
  const { trackEvent } = useTracking();
  const handleTracking = useCallback(() => {
    const [market, industry] = JSON.parse(id);
    trackEvent({
      action: 'click',
      modifier: 'market_industry_list_item',
      industryId: industry,
      market: market,
      name,
      index,
    });
  }, [id, index, name, trackEvent]);
  const callback = useCallback(() => {
    const [market] = JSON.parse(id);
    navigateToScreener(market, slug);
    handleTracking();
  }, [handleTracking, id, slug, navigateToScreener]);
  return typeof canonicalUrl === 'undefined' ? (
    <ButtonLink onClick={callback}>{children}</ButtonLink>
  ) : (
    <RouterLink to={canonicalUrl} onClick={handleTracking} noUnderline>
      {children}
    </RouterLink>
  );
};

const MarketIndustryItem = ({
  id,
  name,
  slug,
  index,
  return7D,
  return7DFormatted,
  return1YrAbs,
  return1YrAbsFormatted,
}: Props) => (
  <Card outline>
    <Click id={id} index={index} name={name} slug={slug}>
      <Details>
        <Name>{name}</Name>
      </Details>
      <Returns>
        <li>
          <span>7D</span>
          <PNV value={return7D}>{return7DFormatted}</PNV>
        </li>
        <li>
          <span>1Y</span>
          <PNV value={return1YrAbs}>{return1YrAbsFormatted}</PNV>
        </li>
      </Returns>
    </Click>
  </Card>
);

export default MarketIndustryItem;
